import i18n, { KeyPrefix, Namespace, ParseKeys, TOptions } from "i18next";
import { FallbackNs, initReactI18next } from "react-i18next";
import LngDetector from "i18next-browser-languagedetector";

import AUTH_FR from "./auth/fr.json";
import AUTH_EN from "./auth/en.json";
import AUTH_DE from "./auth/de.json";
import AUTH_ES from "./auth/es.json";

import ACCOUNTS_FR from "./accounts/fr.json";
import ACCOUNTS_EN from "./accounts/en.json";
import ACCOUNTS_DE from "./accounts/de.json";
import ACCOUNTS_ES from "./accounts/es.json";

import VALIDATION_FR from "./validation/fr.json";
import VALIDATION_EN from "./validation/en.json";
import VALIDATION_DE from "./validation/de.json";
import VALIDATION_ES from "./validation/es.json";

import CANDIDATE_FR from "./candidate/fr.json";
import CANDIDATE_EN from "./candidate/en.json";
import CANDIDATE_DE from "./candidate/de.json";
import CANDIDATE_ES from "./candidate/es.json";

import ORGANIZATION_FR from "./organization/fr.json";
import ORGANIZATION_EN from "./organization/en.json";
import ORGANIZATION_DE from "./organization/de.json";
import ORGANIZATION_ES from "./organization/es.json";

import COUNTRIES_FR from "./countries/fr.json";
import COUNTRIES_EN from "./countries/en.json";
import COUNTRIES_DE from "./countries/de.json";
import COUNTRIES_ES from "./countries/es.json";

import LANGUAGES_FR from "./languages/fr.json";
import LANGUAGES_EN from "./languages/en.json";
import LANGUAGES_DE from "./languages/de.json";
import LANGUAGES_ES from "./languages/es.json";

import RECORD_FR from "./record/fr.json";
import RECORD_EN from "./record/en.json";
import RECORD_DE from "./record/de.json";
import RECORD_ES from "./record/es.json";

import PUBLIC_FR from "./public/fr.json";
import PUBLIC_EN from "./public/en.json";
import PUBLIC_DE from "./public/de.json";
import PUBLIC_ES from "./public/es.json";

import SUBSCRIPTION_FR from "./subscription/fr.json";
import SUBSCRIPTION_EN from "./subscription/en.json";
import SUBSCRIPTION_DE from "./subscription/de.json";
import SUBSCRIPTION_ES from "./subscription/es.json";

import COMMON_FR from "./common/fr.json";
import COMMON_EN from "./common/en.json";
import COMMON_DE from "./common/de.json";
import COMMON_ES from "./common/es.json";

import LINKS_FR from "./links/fr.json";
import LINKS_EN from "./links/en.json";
import LINKS_DE from "./links/de.json";
import LINKS_ES from "./links/es.json";

import { setLocale } from "yup";
import { isDate, Locale } from "date-fns";
import { enGB, fr, de, es } from "date-fns/locale";
import { formatDate } from "../react-helpers/date";

export const i18nResources = {
  fr: {
    auth: AUTH_FR,
    accounts: ACCOUNTS_FR,
    validation: VALIDATION_FR,
    candidate: CANDIDATE_FR,
    organization: ORGANIZATION_FR,
    countries: COUNTRIES_FR,
    languages: LANGUAGES_FR,
    record: RECORD_FR,
    public: PUBLIC_FR,
    subscription: SUBSCRIPTION_FR,
    common: COMMON_FR,
    links: LINKS_FR,
  },
  en: {
    auth: AUTH_EN,
    accounts: ACCOUNTS_EN,
    validation: VALIDATION_EN,
    candidate: CANDIDATE_EN,
    organization: ORGANIZATION_EN,
    countries: COUNTRIES_EN,
    languages: LANGUAGES_EN,
    record: RECORD_EN,
    public: PUBLIC_EN,
    subscription: SUBSCRIPTION_EN,
    common: COMMON_EN,
    links: LINKS_EN,
  },
  de: {
    auth: AUTH_DE,
    accounts: ACCOUNTS_DE,
    validation: VALIDATION_DE,
    candidate: CANDIDATE_DE,
    organization: ORGANIZATION_DE,
    countries: COUNTRIES_DE,
    languages: LANGUAGES_DE,
    record: RECORD_DE,
    public: PUBLIC_DE,
    subscription: SUBSCRIPTION_DE,
    common: COMMON_DE,
    links: LINKS_DE,
  },
  es: {
    auth: AUTH_ES,
    accounts: ACCOUNTS_ES,
    validation: VALIDATION_ES,
    candidate: CANDIDATE_ES,
    organization: ORGANIZATION_ES,
    countries: COUNTRIES_ES,
    languages: LANGUAGES_ES,
    record: RECORD_ES,
    public: PUBLIC_ES,
    subscription: SUBSCRIPTION_ES,
    common: COMMON_ES,
    links: LINKS_ES,
  },
};

const locales: Record<string, Locale> = {
  en: enGB,
  fr: fr,
  de: de,
  es: es,
};

const namespaces = Object.keys(
  i18nResources.fr,
) as unknown as keyof (typeof i18nResources)["fr"];
void i18n
  .use(LngDetector) // detect user language with i18next-browser-languagedetector (from browser, cookie, localStorage, etc)
  .use(initReactI18next) // passes i18n down to react-i18next
  .init({
    resources: i18nResources,
    detection: {
      // it's to convert detected language to the format used in the app
      // ex : lng detected is "fr-FR" but we want to use "fr"
      convertDetectedLanguage: (lng) => lng.split("-")[0],
    },
    ns: namespaces,
    defaultNS: "auth",
    fallbackLng: localStorage.getItem("i18nextLng") ?? "fr",
    interpolation: {
      escapeValue: false, // react already safes from xss
      format: (value, format, lng) => {
        if (isDate(value)) {
          const locale: Locale = locales[lng ?? "fr"];
          return formatDate(value, format, { locale });
        }
        return value;
      },
    },
  });

setLocale({
  mixed: {
    default: translationWithParams("validation:INVALID_FIELD"),
    required: translationWithParams("validation:REQUIRED"),
  },
  string: {
    email: validationTranslation("validation:email.INVALID_EMAIL"),
  },
});

// NOTE: Translation function to use only with setLocale
function translationWithParams<
  N extends Namespace,
  const TOpt extends TOptions,
  const TKey extends ParseKeys<N, TOpt, KPrefix>,
  KPrefix extends KeyPrefix<FallbackNs<N>> = undefined,
>(key: TKey, namespace: Namespace = ["validation"]) {
  return (values: Record<string, unknown>) =>
    validationTranslation<N, TOpt, TKey, KPrefix>(key, values, namespace);
}

export function validationTranslation<
  N extends Namespace,
  const TOpt extends TOptions,
  const TKey extends ParseKeys<N, TOpt, KPrefix>,
  KPrefix extends KeyPrefix<FallbackNs<N>> = undefined,
>(
  key: TKey,
  values: Record<string, unknown> = {},
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _namespace: Namespace = ["validation"],
): string {
  return i18n.t(key as string, values);
}

export default i18n;
